import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { User, LoginForm } from '../types';
import { apiService } from '../services/api';

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: User };

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  isLoading: true,
  isAuthenticated: false,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isLoading: false,
        isAuthenticated: true,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isLoading: false,
        isAuthenticated: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isLoading: false,
        isAuthenticated: false,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
      };
    default:
      return state;
  }
};

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isAssistant: boolean;
  isTechnician: boolean;
  login: (credentials: LoginForm) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check for existing token on app start
  useEffect(() => {
    const initAuth = async () => {
      console.log('🔄 Inicializando autenticación...');
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      console.log('📦 Datos almacenados:', {
        hasToken: !!token,
        hasStoredUser: !!storedUser
      });

      if (token && storedUser) {
        try {
          console.log('✅ Token encontrado, verificando validez...');
          // Verify token is still valid
          const response = await apiService.getCurrentUser();
          console.log('✅ Token válido, usuario obtenido:', response);
          dispatch({ type: 'SET_USER', payload: response });
        } catch (error) {
          console.error('❌ Error verificando token:', error);
          // Token is invalid, clear storage
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          dispatch({ type: 'LOGIN_FAILURE' });
        }
      } else {
        console.log('⚠️ No hay token o usuario almacenado');
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initAuth();
  }, []);

  const login = async (credentials: LoginForm) => {
    try {
      console.log('🔐 Iniciando login...', credentials);
      dispatch({ type: 'LOGIN_START' });

      const response = await apiService.login(credentials);
      console.log('✅ Respuesta del API:', response);

      // La respuesta del backend ya contiene directamente token y user
      const { token, user } = response;
      console.log('📝 Token recibido:', token);
      console.log('👤 Usuario recibido:', user);

      if (!token || !user) {
        console.error('❌ Token o usuario faltante:', { token: !!token, user: !!user });
        throw new Error('Token or user missing from response');
      }

      // Store in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      console.log('💾 Datos guardados en localStorage');

      dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token } });
      console.log('🎉 Login exitoso - estado actualizado');
    } catch (error) {
      console.error('💥 Error en login:', error);
      dispatch({ type: 'LOGIN_FAILURE' });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      dispatch({ type: 'LOGOUT' });
    }
  };

  const updateUser = (user: User) => {
    localStorage.setItem('user', JSON.stringify(user));
    dispatch({ type: 'SET_USER', payload: user });
  };

  const value: AuthContextType = {
    user: state.user,
    token: state.token,
    isLoading: state.isLoading,
    isAuthenticated: state.isAuthenticated,
    isAdmin: state.user?.role === 'admin',
    isAssistant: state.user?.role === 'assistant',
    isTechnician: state.user?.role === 'technician',
    login,
    logout,
    updateUser,
  };

  // Debug log para verificar el estado del usuario
  console.log('🔍 AuthContext Debug:', {
    user: state.user,
    userRole: state.user?.role,
    isAdmin: state.user?.role === 'admin',
    isAssistant: state.user?.role === 'assistant',
    isTechnician: state.user?.role === 'technician',
    isAuthenticated: state.isAuthenticated
  });

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};